import React, { useEffect, useState } from 'react';

import { faCreditCard, faDownload, faLockKeyhole, faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { Box, Container, Divider, Grid, Paper, styled } from '@mui/material';
import { useHistory } from 'react-router-dom';

import bgImageCAPI from '../../../assets/img/candidate/capi-bg.jpg';
import bgLogoCAPI from '../../../assets/img/candidate/capi-logo.png';
import bgLogoOH from '../../../assets/img/candidate/logo_optimhome_2023.svg';
import bgImageOH from '../../../assets/img/candidate/oh-bg.jpg';
import QueryWrapper from '../../../components/QueryWrapper/QueryWrapper';
import auth from '../../../services/Auth';
import contactGQLSchemas from '../../../services/contacts/gqlSchema';
import signatureGQLSchemas from '../../../services/signatures/gqlSchema';
import CandidateService from './CandidateService';
import CandidateInfos from './components/CandidateInfos';
import CandidateNav from './components/CandidateNav';
import AccountCard from './components/TabAccount';
import CandidateCardBankInfo from './components/TabBankInfo';
import CandidateCardContract from './components/TabContract';
import CandidateCardDocuments from './components/TabDocuments';
import { TAB_IDS } from './data/tabs';

const Header = styled(Box)({
  overflow: 'hidden',
  margin: '0',
  position: 'fixed',
  width: '100%',
  height: '250px',
});

const HeaderImg = styled(Box)({
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

const Candidate = () => {
  const history = useHistory();
  const [activeTabId, setActiveTabId] = useState(TAB_IDS.DEFAULT);
  const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
  const [tabs, setTabs] = useState([{ id: TAB_IDS.ACCOUNT, icon: 'fa fa-key', title: 'Mon compte' }]);

  useEffect(() => {
    const isImpersonator = !!auth?.decodedToken?.options?.impersonatorId;
    const hashUpdated = !!auth?.user?.hashUpdated;
    setIsPasswordUpdated(isImpersonator || hashUpdated);
  }, []);

  useEffect(() => {
    if (!isPasswordUpdated) {
      setActiveTabId(TAB_IDS.ACCOUNT);
    } else {
      setActiveTabId(TAB_IDS.CONTRACT);
      setTabs([
        { id: TAB_IDS.CONTRACT, icon: faPenToSquare, title: 'Mon contrat' },
        { id: TAB_IDS.DOCUMENTS, icon: faDownload, title: 'Mes documents' },
        { id: TAB_IDS.BANK, icon: faCreditCard, title: 'Mes informations bancaires' },
        { id: TAB_IDS.ACCOUNT, icon: faLockKeyhole, title: 'Mon compte' },
      ]);
    }
  }, [isPasswordUpdated]);

  if (!auth.isAuthenticated()) {
    history.push('login');

    return null;
  }

  const currentContact = auth.getContact();
  const contactId = currentContact && currentContact.id;
  const contactInfo = currentContact.contactsInfos.find(
    infos => infos.sources && infos.sources.source === 'SALESFORCE',
  );

  const loadContent = (tabId, contact, signaturesByContact) => {
    const tabInfos = tabs.find(tabInfo => tabInfo.id === tabId);

    let signature;
    switch (tabId) {
      default:
      case TAB_IDS.CONTRACT:
        signature = CandidateService.getContractSignature(signaturesByContact);

        return <CandidateCardContract tabInfos={tabInfos} contact={contact} signature={signature} />;
      case TAB_IDS.DOCUMENTS:
        return <CandidateCardDocuments tabInfos={tabInfos} />;
      case TAB_IDS.BANK:
        signature = CandidateService.getSepaSignature(signaturesByContact);

        return <CandidateCardBankInfo tabInfos={tabInfos} contact={contact} signature={signature} />;
      case TAB_IDS.ACCOUNT:
        return (
          <AccountCard
            tabInfos={tabInfos}
            isPasswordUpdated={isPasswordUpdated}
            setIsPasswordUpdated={setIsPasswordUpdated}
          />
        );
    }
  };

  return (
    <QueryWrapper query={contactGQLSchemas.Query.contact} variables={{ id: contactId }}>
      {({ data }) => {
        const contact = data.contact;

        return (
          <QueryWrapper
            query={signatureGQLSchemas.Query.signaturesByContact}
            variables={{ contactId: contactId, type: 'CANDIDATE' }}
          >
            {({ data }) => {
              const dataSignaturesByContact = data.signaturesByContact;

              return (
                <Box>
                  <Header>
                    <HeaderImg
                      sx={{
                        backgroundImage:
                          contactInfo.company.code === 'OPTIMHOME' ? `url(${bgImageOH})` : `url(${bgImageCAPI})`,
                      }}
                    ></HeaderImg>
                  </Header>
                  <Container maxWidth='lg' sx={{ paddingTop: '15px' }}>
                    {contactInfo.company.code === 'OPTIMHOME' ? (
                      <Box sx={{ position: 'relative', maxWidth: '220px' }}>
                        <Box component='img' sx={{ width: '100%', height: 'auto' }} alt='Optimhome' src={bgLogoOH} />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          position: 'relative',
                          backgroundColor: '#fff',
                          boxShadow: '0 5px 10px #aaa',
                          padding: '10px',
                          borderRadius: '5px',
                          maxWidth: '170px',
                        }}
                      >
                        <Box component='img' sx={{ width: '100%', height: 'auto' }} alt='Capifrance' src={bgLogoCAPI} />
                      </Box>
                    )}
                    <Grid container spacing={3} sx={{ marginTop: '32px' }}>
                      <Grid item xs={12} md={5}>
                        <Paper elevation={3} sx={{ position: 'relative', marginBottom: '30px' }}>
                          <Box sx={{ padding: '20px' }}>
                            <CandidateInfos contact={contact} />
                            <Divider sx={{ margin: '1rem 0' }} />
                            <CandidateNav
                              onChangeTab={setActiveTabId}
                              tabs={tabs}
                              signatures={dataSignaturesByContact}
                              contact={contact}
                              defaultActive={activeTabId}
                            />
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <Paper elevation={3} sx={{ position: 'relative', marginBottom: '30px' }}>
                          <Box sx={{ padding: '20px' }}>
                            {loadContent(activeTabId, contact, dataSignaturesByContact)}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Container>
                </Box>
              );
            }}
          </QueryWrapper>
        );
      }}
    </QueryWrapper>
  );
};

export default Candidate;
